import {
  ComponentsPlatformService,
  VirtualManifestAPI,
} from '@wix/editor-platform-manifest-services';
import { CompRef } from '@wix/document-services-types';
import { isAllowedStylePresent } from '../common/actionValidators/actionVisibilityRules';
import { SHADOW_CSS_PROPERTY_TYPES } from '../constants';
import { extractShadowOpacity } from './opacityParser';
import { DATA } from '@wix/component-protocol/schema';
import { hasDataType } from '../../../utils/helpers';
import { checkContextMatch } from '../common/actionValidators/helpers';
import { EditorPointer } from '@wix/editor-document-logic-api';

/**
 * Shadow action validator
 *
 * Rules:
 * - Show if any element has shadow with opacity > 0
 * - Exception: Show if all shadows have opacity = 0 BUT root has boxShadow in manifest
 *   (allows user to add shadow to root, shows "no shadow" preset)
 */
export const createShadowValidator = () => {
  return (
    componentsAPI: ComponentsPlatformService,
    editorPointers: EditorPointer<CompRef>[],
  ): boolean => {
    const hasVectorArtData = hasDataType(
      componentsAPI.components.getData(editorPointers),
      DATA.DATA_TYPE.vectorArt,
    );

    if (hasVectorArtData) {
      return false;
    }

    const shadowProperties = new Set(SHADOW_CSS_PROPERTY_TYPES);

    const rootContexts = editorPointers.map((editorPointer) =>
      componentsAPI.buildManifestAPI({ editorPointers: [editorPointer] }),
    );

    for (const rootContext of rootContexts) {
      if (
        isAllowedStylePresent(rootContext, shadowProperties) ||
        checkContextMatch(rootContext, checkForVisibleShadow)
      ) {
        return true;
      }
    }

    return false;
  };
};

/**
 * Recursively checks if any element in hierarchy has visible shadow
 */
const checkForVisibleShadow = (context: VirtualManifestAPI): boolean => {
  const styles = context.query.getStyles();
  const styleDefinitions = context.resolver.getStyleDefinitions();

  // Check boxShadow in CSS properties
  if (styles?.cssProperties?.boxShadow) {
    const opacity = extractShadowOpacity(styles.cssProperties.boxShadow);
    if (opacity > 0) {
      return true;
    }
  }

  // Check boxShadow in custom properties
  if (styles?.cssCustomProperties && styleDefinitions.cssCustomProperties) {
    for (const [styleId, value] of Object.entries(styles.cssCustomProperties)) {
      const cssPropertyType =
        styleDefinitions.cssCustomProperties[styleId]?.cssPropertyType;

      if (cssPropertyType === 'boxShadow') {
        const opacity = extractShadowOpacity(value);
        if (opacity > 0) {
          return true;
        }
      }
    }
  }

  return false;
};
